/* eslint-disable max-len */
import { CSSProperties, FC } from 'react';
import classNames from 'classnames';
import {
  getContentAlign,
  getSlicePadding,
  htmlSerializerWithProps,
} from '@/utils';
import { PrismicImageWithAlt } from '@/components';
import { IconsRowWithTitleAndDescriptionSliceDefault as IconRow } from '@/types';
import { useFilteredSliceItemsByLocationSlug } from '@/hooks';
import styles from './iconsrowwithtitleanddescription.module.scss';
import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';

const IconsRowWithTitleAndDescription: FC<IconRow> = slice => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);

  const marginBottom = slice?.primary?.bottom_spacing;
  const paddingSection = slice?.primary?.padding_section;
  const iconsPerRow = slice?.primary?.icons_per_row ?? 3;
  const cardMaxWidth = slice?.primary?.card_max_width ?? 300;
  const iconsWidth = slice?.primary?.icons_widht ?? 80;
  const realIconsPerRow = iconsPerRow > 3 ? 'auto-fill' : iconsPerRow;
  const backgroundColor = slice?.primary?.background_color;
  const backgroundImage = slice?.primary?.background_image?.url;
  const backgroundStyleFixed = slice?.primary?.background_style_fixed_cover;
  const minHeight = slice?.primary?.min_heigth;

  const titleText = asText(slice?.primary?.title);
  const descriptionText = asText(slice?.primary?.description);
  const headerAlign = slice?.primary?.text_align ?? 'center';
  const itemsTextAlign = slice?.primary?.items_text_align;
  const itemsAlign =
    !itemsTextAlign || itemsTextAlign === 'same_as_text_align'
      ? headerAlign
      : itemsTextAlign;
  const itemsFlexAlign = getContentAlign(itemsAlign);
  const textColor = slice?.primary?.text_color ?? '';
  const titleStyle = {
    color: textColor,
    maxWidth: '900px',
    textAlign: headerAlign,
  };
  const titleRichTextComponents = htmlSerializerWithProps(
    titleStyle,
    'heading_level-2',
  );
  const descriptionRichTextComponents = htmlSerializerWithProps(titleStyle);

  if (!slice?.primary?.active) {
    return null;
  }

  return (
    <section
      className={classNames(styles.container, getSlicePadding(paddingSection), {
        ['spacing_bottom']: marginBottom,
        ['background__fixed_cover']: backgroundStyleFixed,
        ['background__contained']: !backgroundStyleFixed,
      })}
      style={{
        background: backgroundColor!,
        color: slice?.primary?.text_color ?? '',
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <div
        className={styles.content}
        style={{
          alignItems: getContentAlign(headerAlign),
        }}
      >
        {titleText && (
          <PrismicRichText
            components={titleRichTextComponents}
            field={slice?.primary?.title}
          />
        )}
        {descriptionText && (
          <PrismicRichText
            components={descriptionRichTextComponents}
            field={slice?.primary?.description}
          />
        )}
        <div
          className={classNames(
            styles.logos,
            'iconsrowwithtitleanddescription_logos__grid',
          )}
          style={{
            justifyItems: itemsFlexAlign,
          }}
        >
          {filteredItems?.map((item, index) => {
            return (
              <div
                className={styles.logos__logo}
                key={index}
                style={{
                  alignItems: itemsFlexAlign,
                  textAlign: itemsAlign,
                }}
              >
                <p className={styles.logos__logo__title}>{item?.logo_title}</p>
                <p className={styles.logos__logo__description}>
                  {item?.logo_description}
                </p>
                <figure
                  style={
                    {
                      alignSelf: itemsFlexAlign,
                      '--icons-width': `${iconsWidth}px`,
                    } as CSSProperties
                  }
                >
                  <PrismicImageWithAlt
                    field={item?.logo}
                    height={256}
                    style={{
                      objectFit: 'contain',
                    }}
                    title={item?.logo_title ?? ''}
                    width={256}
                  />
                </figure>
              </div>
            );
          })}
        </div>
        <style>
          {`
          .iconsrowwithtitleanddescription_logos__grid{
            @media (min-width: 1280px) {
                grid-template-columns: repeat(${realIconsPerRow}, minmax(${cardMaxWidth}px, 1fr)) !important;
              }
            }
        `}
        </style>
      </div>
    </section>
  );
};

export default IconsRowWithTitleAndDescription;
